"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowUpRight, Search } from "lucide-react";
import Reveal from "@/components/ui/Reveal";
import { blogPosts } from "@/lib/data";

const categories = ["All", ...new Set(blogPosts.map((p) => p.category))];

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [search, setSearch] = useState("");

  const filteredPosts = useMemo(() => {
    return blogPosts.filter((post) => {
      const categoryMatch =
        selectedCategory === "All" ||
        post.category === selectedCategory;

      const searchMatch =
        post.title.toLowerCase().includes(search.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(search.toLowerCase());

      return categoryMatch && searchMatch;
    });
  }, [selectedCategory, search]);

  const [featured, ...rest] = filteredPosts;

  return (
    <>
      <section className="bg-accent/40 pb-14 pt-16 lg:pt-24">
        <div className="container-px mx-auto max-w-3xl text-center">
          <Reveal>
            <span className="eyebrow">Blog</span>
            <h1 className="mt-6 text-4xl font-bold tracking-tight text-ink sm:text-5xl">
              Ideas on modern mobile support
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-lg text-muted">
              Field notes on remote support, security, and cutting resolution time — from the team building
              Mobilink.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section-py bg-white">
        <div className="container-px mx-auto max-w-7xl">

          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">

            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${selectedCategory === cat
                    ? "border-primary bg-primary text-white"
                    : "border-hairline text-muted hover:border-primary hover:text-primary"
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="relative w-full sm:w-72">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />

              <input
                type="search"
                placeholder="Search articles..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="focus-ring w-full rounded-full border border-hairline py-3 pl-11 pr-4 text-sm"
              />
            </div>

          </div>

          {featured && (
            <Reveal className="mt-10">
              <Link
                href={`/blog/${featured.slug}`}
                className="group grid gap-6 rounded-3xl border border-hairline bg-accent/40 p-8 lg:grid-cols-[1.1fr_1fr]"
              >
                <div className="relative aspect-[16/10] overflow-hidden rounded-2xl">
                  <Image
                    src="/logo/logo2.png"
                    alt={featured.title}
                    fill
                    className="object-cover"
                  />
                </div>

                <div>
                  <span className="eyebrow">{featured.category}</span>

                  <h2 className="mt-4 text-3xl font-bold">
                    {featured.title}
                  </h2>

                  <p className="mt-3 text-muted">
                    {featured.excerpt}
                  </p>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((post) => (
              <Reveal key={post.slug}>
                <Link
                  href={`/blog/${post.slug}`}
                  className="group flex h-full flex-col rounded-3xl border border-hairline bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40"
                >
                  <span className="eyebrow">{post.category}</span>

                  <h3 className="mt-5 text-lg font-semibold">
                    {post.title}
                  </h3>

                  <p className="mt-3 flex-1 text-sm text-muted">
                    {post.excerpt}
                  </p>

                  <div className="mt-6 flex items-center justify-between border-t pt-4 text-xs">
                    <span>{post.readTime}</span>

                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="py-16 text-center text-muted">
              No articles found.
            </div>
          )}

        </div>
      </section>
    </>
  );
}